import React, { useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { formatRelativeDate } from '@/utils/formatRelativeDate';
import { isOutgoingTransfer, paginate } from '@/utils/transfers';
import { listMyTransfersApi, type ApiCreditTransfer } from '@/api/vendo';

const PAGE_SIZE = 10;

/**
 * The current user's credit-transfer log (sent + received), paginated.
 * Shared by My Points, the Distributor page, and the Reseller page so the
 * listing looks the same everywhere.
 */
const CreditTransfersTable: React.FC = () => {
   const { data, isLoading } = useQuery({
      queryKey: ['vendo', 'my-transfers'],
      queryFn: listMyTransfersApi,
   });
   const me = data?.me ?? '';
   const transfers: ApiCreditTransfer[] = data?.transfers ?? [];
   const [page, setPage] = useState(1);

   if (isLoading) return <SkeletonLines lines={3} />;
   if (transfers.length === 0)
      return (
         <Empty>No credit transfers yet. Transfers you send or receive appear here.</Empty>
      );

   const { pageRows: rows, totalPages, safePage } = paginate(
      transfers,
      page,
      PAGE_SIZE
   );

   return (
      <>
         <Table>
            <Head>
               <Cell>Direction</Cell>
               <Cell>Counterparty</Cell>
               <Cell $right>Credits</Cell>
               <Cell>Note</Cell>
               <Cell $right>When</Cell>
            </Head>
            {rows.map((t) => {
               const outgoing = isOutgoingTransfer(t, me);
               return (
                  <Row key={t._id}>
                     <Cell>
                        <DirPill $out={outgoing}>
                           {outgoing ? '↑ Sent' : '↓ Received'}
                        </DirPill>
                     </Cell>
                     <Cell>{outgoing ? t.toEmail : t.fromEmail}</Cell>
                     <CellRight $color={outgoing ? colors.DANGER : colors.SUCCESS}>
                        {outgoing ? '-' : '+'}
                        {t.amount.toLocaleString()}
                     </CellRight>
                     <Cell>{t.note || '—'}</Cell>
                     <CellRight $muted>{formatRelativeDate(t.createdAt)}</CellRight>
                  </Row>
               );
            })}
         </Table>
         {totalPages > 1 && (
            <Pager>
               <PagerBtn
                  type="button"
                  disabled={safePage <= 1}
                  onClick={() => setPage(safePage - 1)}
               >
                  ← Prev
               </PagerBtn>
               <PagerInfo>
                  {safePage} / {totalPages}
               </PagerInfo>
               <PagerBtn
                  type="button"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage(safePage + 1)}
               >
                  Next →
               </PagerBtn>
            </Pager>
         )}
      </>
   );
};

export default CreditTransfersTable;

const Muted = styled.p`
   font-size: 13px;
   color: ${colors.GRAY5};
   padding: 12px 0;
`;
const Empty = styled.div`
   padding: 24px 16px;
   text-align: center;
   font-size: 13px;
   color: ${colors.GRAY5};
   background: ${colors.WHITE};
   border: 1px dashed ${colors.GRAY12};
   border-radius: 12px;
`;
const Table = styled.div`
   display: flex;
   flex-direction: column;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   overflow: hidden;
`;
const gridCols = '0.9fr 1.6fr 0.7fr 1.4fr 0.8fr';
const Head = styled.div`
   display: grid;
   grid-template-columns: ${gridCols};
   gap: 10px;
   padding: 9px 12px;
   background: ${colors.GRAY14};
   border-bottom: 1px solid ${colors.GRAY13};
   font-size: 10px;
   font-weight: 800;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${colors.GRAY5};
   /* The 5 tiny columns are unreadable on a phone; the rows restack into
      cards below, so the header is dropped. */
   @media (max-width: 640px) {
      display: none;
   }
`;
const Row = styled.div`
   display: grid;
   grid-template-columns: ${gridCols};
   gap: 10px;
   padding: 10px 12px;
   align-items: center;
   border-bottom: 1px solid ${colors.GRAY14};
   font-size: 13px;
   color: ${colors.MAIN_DARK};
   &:last-child { border-bottom: none; }
   /* Mobile: stack into a card. Cells are (1)direction (2)counterparty
      (3)credits (4)note (5)when — lay them out by grid area. */
   @media (max-width: 640px) {
      grid-template-columns: 1fr auto;
      grid-template-areas:
         'dir credits'
         'who who'
         'note when';
      row-gap: 4px;
      column-gap: 10px;
      padding: 12px;
      & > *:nth-child(1) {
         grid-area: dir;
      }
      & > *:nth-child(2) {
         grid-area: who;
         word-break: break-all;
         color: ${colors.GRAY5};
         font-size: 12px;
      }
      & > *:nth-child(3) {
         grid-area: credits;
      }
      & > *:nth-child(4) {
         grid-area: note;
         text-align: left;
         color: ${colors.GRAY5};
         font-size: 12px;
      }
      & > *:nth-child(5) {
         grid-area: when;
      }
   }
`;
const Cell = styled.div<{ $right?: boolean }>`
   min-width: 0;
   text-align: ${({ $right }) => ($right ? 'right' : 'left')};
   word-break: break-word;
`;
const CellRight = styled.div<{ $color?: string; $muted?: boolean }>`
   text-align: right;
   white-space: nowrap;
   font-weight: ${({ $muted }) => ($muted ? 500 : 800)};
   color: ${({ $color, $muted }) =>
      $color ?? ($muted ? colors.GRAY5 : colors.MAIN_DARK)};
`;
const DirPill = styled.span<{ $out: boolean }>`
   font-size: 11px;
   font-weight: 700;
   padding: 3px 9px;
   border-radius: 999px;
   white-space: nowrap;
   color: ${({ $out }) => ($out ? colors.DANGER : colors.SUCCESS)};
   background: ${({ $out }) =>
      $out ? `${colors.DANGER}14` : `${colors.SUCCESS}14`};
`;
const Pager = styled.div`
   display: flex;
   align-items: center;
   justify-content: flex-end;
   gap: 10px;
   padding-top: 8px;
`;
const PagerBtn = styled.button`
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   padding: 5px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 7px;
   background: ${colors.WHITE};
   color: ${colors.GRAY3};
   cursor: pointer;
   &:disabled { opacity: 0.4; cursor: default; }
`;
const PagerInfo = styled.span`
   font-size: 12px;
   color: ${colors.GRAY5};
   font-weight: 700;
`;
